import { AskUserQuestionCard } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/components/ask-user-question-card";
import { CompletedOperationCard } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/components/completed-operation-card";
import { TableCard } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/components/table-card";
import type { TableItem } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/hooks/use-intelligent-inventory";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import * as styles from "./inventory-code-mode-cards.module.css";
import type {
  InventoryCodeModeCardsData,
  UnsupportedInventoryChart,
} from "./parse-inventory-code-mode";

export const InventoryCodeModeCards = memo(function InventoryCodeModeCards({
  data,
  disabled,
  onQuestionResponse,
}: {
  data: InventoryCodeModeCardsData;
  disabled?: boolean;
  onQuestionResponse?: (toolCallId: string, result: unknown) => void;
}) {
  return (
    <Flex dir="column" gap="md" minW="none" w="full">
      {data.operations.map((operation, index) => {
        // Ask Redo intentionally does not expose inventory Undo.
        return (
          <CompletedOperationCard
            key={`operation-${operation.toolCallId ?? index}-${index}`}
            operation={operation}
          />
        );
      })}
      {data.questions.map((question, index) => (
        <AskUserQuestionCard
          disabled={disabled ?? !onQuestionResponse}
          item={question}
          key={`question-${question.toolCallId || index}`}
          onRespond={onQuestionResponse ?? (() => {})}
        />
      ))}
      {data.tables.map((table, index) => (
        <ExpandableTableCard
          item={table}
          key={`table-${table.toolCallId ?? index}-${index}`}
        />
      ))}
      {data.chartMessages.map((chart, index) => (
        <UnsupportedChartMessage
          chart={chart}
          key={`chart-${chart.toolCallId ?? index}-${index}`}
        />
      ))}
    </Flex>
  );
});

/** Matches `--redo-state-duration`; the modal has no transition hook to
 *  wait on, so unmounting is delayed to let the closing keyframes run. */
const OVERLAY_ANIMATION_MS = 200;

/**
 * Inline chat table whose expand affordance opens the same table in a
 * modal overlay — never a navigation, which would interrupt streaming.
 */
const ExpandableTableCard = memo(function ExpandableTableCard({
  item,
}: {
  item: TableItem;
}) {
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openOverlay = useCallback(() => {
    clearCloseTimer();
    setClosing(false);
    setExpanded(true);
  }, [clearCloseTimer]);

  const closeOverlay = useCallback(() => {
    setClosing(true);
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setExpanded(false);
      setClosing(false);
      closeTimer.current = null;
    }, OVERLAY_ANIMATION_MS);
  }, [clearCloseTimer]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  return (
    <>
      <TableCard item={item} onExpand={openOverlay} />
      {expanded && (
        <RedoModal
          className={
            closing ? styles.overlayPanelClosing : styles.overlayPanelOpening
          }
          headerBorder
          hideFooter
          modalSize="xl"
          muiClassName={
            closing ? styles.overlayRootClosing : styles.overlayRootOpening
          }
          onModalCloseRequested={closeOverlay}
          title={item.title ?? "Table"}
        >
          <TableCard expandedView item={item} />
        </RedoModal>
      )}
    </>
  );
});

const UnsupportedChartMessage = memo(function UnsupportedChartMessage({
  chart,
}: {
  chart: UnsupportedInventoryChart;
}) {
  const label = [chart.title, chart.chartType].filter(Boolean).join(" - ");

  return (
    <Flex
      className={styles.chartMessageCard}
      dir="column"
      gap="xxs"
      px="lg"
      py="md"
      w="full"
    >
      <Text fontSize="sm" fontWeight="semibold">
        Chart not shown in Ask Redo
      </Text>
      {label ? (
        <Text fontSize="xs" textColor="secondary">
          {label}
        </Text>
      ) : null}
      <Text fontSize="xs" textColor="secondary">
        {chart.message}
      </Text>
    </Flex>
  );
});
